import {
  IonIcon,
  IonItem,
  IonRow,
  IonSpinner,
  IonText,
  useIonToast,
} from '@ionic/react';
import axios, { AxiosError } from 'axios';
import HStack from 'components/HStack';
import VStack from 'components/VStack';
import {
  cloudDownloadOutline,
  cloudUploadOutline,
  eye,
  eyeOff,
  star,
  starOutline,
  videocam,
  download,
} from 'ionicons/icons';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { ST } from 'types';
import { useStores } from '../../../models';
import { RunModel } from '../../../models/RunModel/RunModel';
import { useCalculationsAPI } from '../../../services/freak_estimator/Estimator';
import { useUnitConversion } from '../../../hooks/useUnitConversion';

import { useGeoLocation } from '../../../services/GeoLocation';
import { makeErrorToast, toSpeed } from '../../../util';
import './RunListItem.css';
import Icon from '../../icons/Icon';
import { logger } from 'services/Logger';
import { present } from '@ionic/core/dist/types/utils/overlays';
import { MESSAGE_ALIVE_MS, MESSAGE_POSITION, VIDEO_URL } from 'util/constants';
import {
  buildOverlayDataFromPlay,
  freakVideoService,
} from '../../../services/freak_video';
import { Filesystem, Directory } from '@capacitor/filesystem';

interface RunListItemProps {
  onDeletePlay?: (trick: RunModel) => Promise<void>;
  item: RunModel;
  isFriend: boolean;
  isPublic: boolean;
  directNavigation?: boolean; // New prop for Profile page behavior
  onClick?: (item: RunModel) => void; // NEU: für modale Navigation
}

export const scaleData = (
  value: number | undefined,
  suffix: string = ''
): string => {
  if (value === undefined) return `?? ${suffix}`;
  if (value < 1 && suffix === 'm') {
    suffix = 'cm';
    value *= 100;
  }

  return `${value.toFixed(2)} ${suffix}`;
};

export const calcHighestSpeed = (steps?: ST.PlayStep[]): number | undefined => {
  if (!steps) return undefined;

  return steps.reduce((highest: number, current: ST.PlayStep) => {
    return Math.max(highest, toSpeed(current.v_ms));
  }, 0);
};

// TODO: The 'isFriend' field is used to enable/disable the possiblity
// to edit the RunListItem. However, runs are not necessarily only by the user or
// a friend, but can be a public one. We should rename it to 'canEdit' for a clearer
// naming.
const RunListItem: React.FC<RunListItemProps> = observer(
  ({
    item,
    onDeletePlay,
    isPublic,
    isFriend,
    directNavigation = false,
    onClick,
  }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const estimator = useCalculationsAPI();
    const locator = useGeoLocation();
    const { currentUser, appData } = useStores();
    const unitConversion = useUnitConversion();
    const [isLoading, setIsLoading] = useState(false);
    const [toast, dismiss] = useIonToast();
    const [location, setLocation] = useState(t('UNKNOWN_LOCATION') as string);
    const [isNavigating, setIsNavigating] = useState(false);
    const lastClickTimeRef = useRef<number>(0);
    const [present] = useIonToast();

    const theLocation = item.recordData?.location ?? item.playData?.location;
    const theTitle = item.recordData?.title ?? item.playData?.title ?? '??';

    useEffect(() => {
      // Only auto-download for authenticated users and if the content exists
      if (currentUser && !item.playData && item.playUrl) {
        item.downloadPlayData().catch((error) => {
          logger.warn(
            'Failed to download playData for',
            item.id,
            error.message
          );
        });
      }
      if (currentUser && !item.localVideoUri && item.videoUrl) {
        logger.info('⬇Downloading video for:', item.id);
        item.downloadVideo().catch((error) => {
          logger.warn('Failed to download video for', item.id, error.message);
        });
      }
    }, [currentUser, item]);

    useEffect(() => {
      if (!theLocation) {
        setLocation(t('UNKNOWN_LOCATION') as string);
        return;
      }
      if (theLocation.name) {
        setLocation(theLocation.name);
        return;
      }

      locator
        .getAddressFromLocation(theLocation)
        .then(setLocation)
        .catch(() => {});
    }, [theLocation, locator, t]);

    const navigateToDetailPageOrUpload = useCallback(async () => {
      if (isNavigating) {
        return;
      }
      setIsNavigating(true);
      try {
        if (!item.playData || !item.localVideoUri) {
          setIsLoading(true);
          try {
            if (item.playUrl) {
              if (!item.playData) {
                await item.downloadPlayData();
              }
              if (!item.localVideoUri) {
                await item.downloadVideo();
              }
            } else {
              await estimator.submitRecordData(item);
              history.push(`/trickpreview/${item.id}`);
              return;
            }
          } catch (e: any) {
            let msg = e.message ?? e;
            if (e instanceof AxiosError && e.response?.status === 402) {
              msg = (
                <Trans
                  i18nKey="NO_ACTIVE_LICENSE_ERROR"
                  defaults="Du hast keine aktive Lizenz!"
                />
              );
            } else if (
              e.message?.includes('unauthorized') ||
              e.message?.includes('permission')
            ) {
              msg = (
                <Trans
                  i18nKey="FIREBASE_PERMISSION_ERROR"
                  defaults="Keine Berechtigung zum Laden der Run-Daten. Bitte melden Sie sich an."
                />
              );
            }
            toast(makeErrorToast(t('ERROR'), msg, dismiss));
            return;
          } finally {
            setIsLoading(false);
          }
        }
        history.push(`/trick/${item.id}`);
      } finally {
        // Reset navigation flag after a delay to allow navigation to complete
        setTimeout(() => {
          setIsNavigating(false);
        }, 1000);
      }
    }, [item, estimator, history, toast, dismiss, t, isNavigating]);

    const starClicked = (ev: React.MouseEvent) => {
      ev.preventDefault();
      ev.stopPropagation();
      if (!item.shareUrl || !currentUser) return;

      currentUser.toggleFavorite(item.shareUrl).then((ret) => {
        if (ret == null) return; // error thrown.
        item!.setFavorite(ret);
      });
      return false;
    };

    const togglePublicVisibility = async (ev: React.MouseEvent) => {
      ev.preventDefault();
      ev.stopPropagation();
      if (!isFriend) {
        // Only allow toggling for own tricks (not friend's tricks)
        try {
          await item.togglePublic();
        } catch (error) {
          // Trans-Nachricht als String für Toast (wie in MapComponent)
          let messageError = '';
          const transElement = Trans({
            i18nKey: 'ERROR_TOGGLE_PUBLIC',
            defaultValue: 'Error toggling public visibility',
          });
          const children =
            (transElement &&
              typeof transElement === 'object' &&
              (transElement as any).props?.children) ||
            undefined;
          if (typeof children === 'string') {
            messageError = children;
          } else if (Array.isArray(children)) {
            messageError = children.join('');
          } else if (children) {
            messageError = String(children);
          } else {
            messageError = 'Error toggling public visibility';
          }

          present({
            message: messageError,
            duration: MESSAGE_ALIVE_MS,
            color: 'warning',
            position: MESSAGE_POSITION,
          });
        }
      }
      return false;
    };

    const downloadData = async (ev: React.MouseEvent) => {
      ev.preventDefault();
      ev.stopPropagation();

      if (!item.playData) {
        // Trans-Nachricht als String für Toast (wie in MapComponent)
        let messageNoPlayData = '';
        const transElement = Trans({
          i18nKey: 'NO_PLAY_DATA_AVAILABLE',
          defaultValue: 'No play data available',
        });
        const children =
          (transElement &&
            typeof transElement === 'object' &&
            (transElement as any).props?.children) ||
          undefined;
        if (typeof children === 'string') {
          messageNoPlayData = children;
        } else if (Array.isArray(children)) {
          messageNoPlayData = children.join('');
        } else if (children) {
          messageNoPlayData = String(children);
        } else {
          messageNoPlayData = 'No play data available';
        }

        present({
          message: messageNoPlayData,
          duration: MESSAGE_ALIVE_MS,
          color: 'warning',
          position: MESSAGE_POSITION,
        });
        return false;
      }

      try {
        const { displaySettings } = appData;
        let fileName = theTitle || 'run';
        const overlayData = buildOverlayDataFromPlay(item.playData);
        const downloadType = displaySettings.downloadType || 'all';

        // Video-Download
        if (downloadType === 'only_video' || downloadType === 'all') {
          if (item.videoUrl || item.localVideoUri) {
            try {
              if (item.videoUrl && !item.localVideoUri) {
                await item.downloadVideo();
              }
              let fileInfo;
              const uri = item.localVideoUri!;
              const filename = uri.split('/').pop() || `${fileName}.mp4`;
              fileInfo = { directory: Directory.Data, path: filename };
              const readResult = await Filesystem.readFile(fileInfo);
              let base64Data: string;
              if (typeof readResult.data === 'string') {
                base64Data = readResult.data;
              } else {
                base64Data = await new Promise<string>((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onload = () => {
                    const result = reader.result as string;
                    const base64 = result.includes(',')
                      ? result.split(',')[1]
                      : result;
                    resolve(base64);
                  };
                  reader.onerror = reject;
                  reader.readAsDataURL(readResult.data as Blob);
                });
              }
              const binaryString = atob(base64Data);
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }
              const videoBlob = new Blob([bytes], { type: 'video/mp4' });
              const videoUrl = URL.createObjectURL(videoBlob);
              const videoLink = document.createElement('a');
              videoLink.href = videoUrl;
              const name = item.playData.title || 'Run X';
              videoLink.download = name;
              document.body.appendChild(videoLink);
              videoLink.click();
              document.body.removeChild(videoLink);
              URL.revokeObjectURL(videoUrl);
            } catch (videoError) {
              logger.warn('Video download error for', item.id, videoError);
            }
          }
        }

        // JSON-Download
        if (downloadType !== 'only_video') {
          const enhancedData: any = {
            metadata: {
              riderName: currentUser?.displayName || 'Unknown Rider',
              company: 'SPINNAX GmbH',
              contactEmail: 'info@spinnax.com',
              runTimestamp: item.date.toISOString(),
              exportTimestamp: new Date().toISOString(),
              license: 'MIT',
              licenseText:
                'Copyright (c) 2025 SPINNAX GmbH\n\nPermission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:\n\nThe above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.\n\nTHE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.',
            },
          };
          if (downloadType === 'only_telemetry' || downloadType === 'all') {
            enhancedData.telemetry = [
              {
                time: 'Time in seconds since start of run',
                jump_distance: 'Horizontal jump distance in centimeters',
                distance: 'Total distance traveled in centimeters',
                jump_height: 'Jump height in centimeters',
                height_difference_up: 'Height difference (ascent) in centimeters',
                airtime: 'Airtime in seconds',
                speed: 'Speed in m/s',
                figure: 'Name of detected figure/trick (if any)'
              },
              ...(overlayData.values || [])
            ];
          }
          if (downloadType === 'only_steps' || downloadType === 'all') {
            enhancedData.steps = [
              {
                t_ms: 'Timestamp in ms from start of recording',
                q: 'Orientation quaternion (w, x, y, z components)',
                s_m: 'Distance made in the timestep in meters',
                v_ms: 'Estimated velocity vector in m/s (x, y, z components)',
                a_ms2: 'Estimated acceleration vector in m/s² (x, y, z components)',
                q_uvw_rst: 'Quaternion rotated to travel direction (w, u, v, r, s, t)'
              },
              ...(item.playData.steps || [])
            ];
          }
          if (downloadType === 'only_figures' || downloadType === 'all') {
            enhancedData.figures = [
              {
                name: 'Name of the trick/figure',
                i_beg: 'Start index in steps array where figure begins',
                i_end: 'End index in steps array where figure ends',
                score: 'Computed score for this figure (0-100)',
                angle: 'Rotation angle of the trick in degrees',
                has_backside: 'Boolean indicating if trick was performed backside',
                stance: 'Riding stance (regular, goofy, switch, etc.)',
                achieved: 'Boolean indicating if trick was successfully completed',
                details: 'Additional technical details about the figure execution'
              },
              ...(item.playData.figures || [])
            ];
          }
          if (downloadType === 'only_details' || downloadType === 'all') {
            enhancedData.details = [
              {
                board_grabbed: 'Whether the board was grabbed during the run (boolean)',
                fly_height_m: 'Maximum height reached above starting level (meters)',
                fly_width_m: 'Maximum horizontal distance traveled while airborne (meters)',
                height_difference_m: 'Net height change from start to end (meters)',
                travel_distance_m: 'Total distance traveled during the run (meters)',
                air_time_s: 'Total time spent airborne (seconds)',
                slide_time_s: 'Total time spent sliding/grinding (seconds)',
                wheelie_time_s: 'Total time spent in wheelie/manual position (seconds)',
                acceleration_uvw_ms2: 'Maximum acceleration in board coordinates (object: x, y, z)',
                rotation_speed_max_uvw_rad_s: 'Maximum rotation speed in board coordinates (object: x, y, z)',
                rotation_count_uvw: 'Total rotation count in board coordinates (object: x, y, z)'
              },
              ...(item.playData.details ? [item.playData.details] : [])
            ];
          }
          const jsonData = JSON.stringify(enhancedData, null, 2);
          const blob = new Blob([jsonData], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${fileName}.json`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      } catch (error) {
        // Error message
        let messageError = '';
        const errorTransElement = Trans({
          i18nKey: 'DOWNLOAD_FAILED',
          defaultValue: 'Download failed',
        });
        const errorChildren =
          (errorTransElement &&
            typeof errorTransElement === 'object' &&
            (errorTransElement as any).props?.children) ||
          undefined;
        if (typeof errorChildren === 'string') {
          messageError = errorChildren;
        } else if (Array.isArray(errorChildren)) {
          messageError = errorChildren.join('');
        } else if (errorChildren) {
          messageError = String(errorChildren);
        } else {
          messageError = 'Download failed';
        }

        present({
          message: messageError,
          duration: MESSAGE_ALIVE_MS,
          color: 'danger',
          position: MESSAGE_POSITION,
        });
      }
      return false;
    };

    const details = item.playData?.details;

    const itemInfo = useMemo(() => {
      // Use unit conversion for all distance measurements
      let jumpDistObj = unitConversion.scaleDistance(details?.fly_width_m);
      let distObj = unitConversion.scaleDistance(details?.travel_distance_m);
      let jumpHeightObj = unitConversion.scaleDistance(details?.fly_height_m);
      let heightDiffObj = unitConversion.scaleDistance(
        details?.height_difference_m
      );

      // Extract formatted strings from objects
      let jumpDist = `${jumpDistObj.value}${jumpDistObj.suffix}`;
      let dist = `${distObj.value}${distObj.suffix}`;
      let jumpHeight = `${jumpHeightObj.value}${jumpHeightObj.suffix}`;
      let heightDiff = `${heightDiffObj.value}${heightDiffObj.suffix}`;
      let airTime = scaleData(details?.air_time_s, 's');

      // Use unit conversion for speed display
      const speedValue = calcHighestSpeed(item.playData?.steps);
      let speed = '?? km/h';
      if (speedValue !== undefined) {
        const displayData = unitConversion.getDisplay(
          speedValue,
          'speed',
          'metric'
        ); // km/h is stored in metric
        speed = displayData.formatted;
      }

      return { jumpDist, dist, jumpHeight, heightDiff, airTime, speed };
    }, [details, item.playData?.steps, unitConversion]);

    const unDownloaded =
      (!item.playData && item.playUrl) ||
      (!item.localVideoUri && item.videoUrl);
    // For public tricks from other users, we should not show them as unanalyzed
    // Only show as unanalyzed if it's the user's own trick (!isFriend) and has no playData/playUrl
    const unAnalyzed = !isFriend && !item.playData && !item.playUrl;
    return (
      <IonItem className="trick-list-item" button={false}>
        {(unDownloaded || unAnalyzed) && (
          <div
            id="trick-list-item-overlay"
            onClick={(e) => {
              const now = Date.now();
              if (now - lastClickTimeRef.current < 1000) {
                e.stopPropagation();
                e.preventDefault();
                return;
              }
              lastClickTimeRef.current = now;

              e.stopPropagation();
              e.preventDefault();

              if (isNavigating) {
                return;
              }

              // Check if onClick handler should be used instead
              if (onClick) {
                onClick(item);
                return;
              }

              navigateToDetailPageOrUpload();
            }}
            style={{
              pointerEvents: 'none',
              zIndex: 1,
            }}
          >
            <div
              style={{ height: '100%', width: '100%', pointerEvents: 'auto' }}
            >
              {unDownloaded && <IonIcon icon={cloudDownloadOutline} />}
              {unAnalyzed && <IonIcon icon={cloudUploadOutline} />}
            </div>
          </div>
        )}

        <div
          style={{ height: '100%', width: '100%', padding: '16px 0px' }}
          onClick={(e) => {
            const now = Date.now();
            if (now - lastClickTimeRef.current < 1000) {
              e.stopPropagation();
              e.preventDefault();
              return;
            }
            lastClickTimeRef.current = now;

            e.stopPropagation();
            e.preventDefault();

            if (isNavigating) {
              return;
            }

            if (onClick) {
              onClick(item);
              return;
            }
            if (directNavigation) {
              setIsNavigating(true);
              history.push(`/trick/${item.id}`);
              setTimeout(() => setIsNavigating(false), 1000);
            } else {
              navigateToDetailPageOrUpload();
            }
          }}
        >
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <IonRow className="ion-justify-content-between ion-align-items-center">
              <IonRow
                style={{
                  gap: '8px',
                  justifyContent: 'flex-end',
                  width: '100%',
                }}
              >
                {/* VIDEO ICON - linksbündig */}
                {item.videoUrl && (
                  <IonIcon
                    icon={videocam}
                    size="large"
                    color="white"
                    style={{
                      fontSize: '2rem',
                      width: '2rem',
                      height: '2rem',
                      marginRight: 'auto',
                    }}
                  />
                )}
                {/* Die anderen Icons bleiben rechts */}
                {/* DOWNLOAD BUTTON - nur für Web und nicht für öffentliche Runs */}
                {process.env.REACT_APP_IS_WEB === 'true' &&
                  item.playData &&
                  !isPublic && (
                    <IonIcon
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        downloadData(e);
                      }}
                      size="large"
                      color="secondary"
                      icon={download}
                      style={{
                        cursor: 'pointer',
                        fontSize: '2rem',
                        width: '2rem',
                        height: '2rem',
                      }}
                    />
                  )}
                {/* FAVORIT ICON */}
                {item.playData && (
                  <IonIcon
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      starClicked(e);
                    }}
                    size="large"
                    color="secondary"
                    icon={item.favorite ? star : starOutline}
                    style={{
                      cursor: 'pointer',
                      fontSize: '2rem',
                      width: '2rem',
                      height: '2rem',
                    }}
                  />
                )}
                {/* PUBLIC ICON */}
                {!isFriend && (
                  <IonIcon
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      togglePublicVisibility(e);
                    }}
                    size="large"
                    color="secondary"
                    icon={item.isPublic ? eye : eyeOff}
                    style={{
                      cursor: 'pointer',
                      fontSize: '2rem',
                      width: '2rem',
                      height: '2rem',
                    }}
                  />
                )}
                {onDeletePlay && !isPublic && !isFriend && (
                  <Icon
                    name="DeleteIcon"
                    size="large"
                    color="--ion-color-secondary"
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        await onDeletePlay(item);
                      } catch (error) {
                        logger.error('Failed to delete play:', error);
                      }
                    }}
                  />
                )}
              </IonRow>
            </IonRow>
            {/* TITEL */}
            <IonRow className="ion-justify-content-between ion-align-items-center">
              <IonRow style={{ gap: '4px', fontSize: 24 }}>
                <IonText>
                  <b>{theTitle}</b>
                </IonText>
              </IonRow>
            </IonRow>

            <HStack>
              <div className="trick-stats">
                {/* JUMP HEIGHT */}
                <div className="trick-stat">
                  <IonIcon src="/assets/icons/jump_height.svg" />
                  <IonText>{itemInfo.jumpHeight}</IonText>
                </div>
                {/* JUMP DISTANCE */}
                <div className="trick-stat">
                  <IonIcon src="/assets/icons/jump_distance.svg" />
                  <IonText> {itemInfo.jumpDist} </IonText>
                </div>
                {/* SPEED */}
                <div className="trick-stat">
                  <IonIcon src="/assets/icons/speed2.svg" />
                  <IonText>{itemInfo.speed}</IonText>
                </div>
              </div>
            </HStack>
            <VStack>
              <HStack>
                <div style={{ flexGrow: '1' }} />
                {isFriend && (
                  <IonText className="secondary-text">{item.skater}</IonText>
                )}
              </HStack>
              <HStack style={{ justifyContent: 'space-between' }}>
                <IonText
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {location}
                </IonText>
                <IonText style={{ marginLeft: '4px', whiteSpace: 'nowrap' }}>
                  {item.date.toLocaleString('de-DE', {
                    day: '2-digit',
                    month: '2-digit',
                    year: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </IonText>
              </HStack>
            </VStack>
          </div>

          {isLoading && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
              }}
            >
              <IonSpinner name="crescent" style={{ width: 48, height: 48 }} />
            </div>
          )}
        </div>
      </IonItem>
    );
  }
);

export default RunListItem;
